import React, { useState } from "react";
import {
    Button,
    Menu,
    MenuItem,
    CircularProgress,
    ListSubheader,
    Divider,
    Typography,
    Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ExportHmiButton from "./ExportHmiButton";
import ExportTextButton from "./ExportTextButton";
import { pdfFileNameForLanguage } from "../constants/languages";
import { useExportCodePreference } from "../context/ExportCodePreferenceContext";
import useGetUserRoleFlags from "../hooks/useGetUserRoleFlags";

interface ExportMenuProps {
    language: string;
}

/** Must match backend `MODE_APPROVED_PLUS_OWN_UNAPPROVED`. */
const EXPORT_MODE_APPROVED_PLUS_OWN_UNAPPROVED = "approved_plus_own_unapproved";

const EXPERIMENTAL_EXPORT_TOOLTIP =
    "Approved events + your own unapproved drafts (created_by). Not for production rollout.";

const ExportMenu: React.FC<ExportMenuProps> = ({ language }) => {
    const { exportUseOldCodeParam } = useExportCodePreference();
    const { isAdmin, isCreator } = useGetUserRoleFlags();
    const showExperimentalExports = isAdmin || isCreator;
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [loading, setLoading] = useState(false); // Export läuft?
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Callback für Buttons
    const handleExportStart = () => {
        setLoading(true);
    };

    const handleExportEnd = () => {
        setLoading(false);
        handleClose();
    };

    return (
        <>
            <Button
                variant="contained"
                startIcon={<FileDownloadIcon />}
                endIcon={loading ? <CircularProgress size={20} color="inherit" /> : <ExpandMoreIcon />}
                onClick={handleClick}
                disabled={loading}
            >
                {loading ? "Exporting..." : "Export"}
            </Button>
            {!loading &&
                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={handleClose}
                    sx={{
                        "& .MuiMenuItem-root .MuiButton-root": {
                            width: "100%",
                            justifyContent: "flex-start",
                            textTransform: "none",
                        },
                    }}
                >
                    <ListSubheader
                        sx={{
                            lineHeight: 1.35,
                            py: 1,
                            whiteSpace: "normal",
                        }}
                    >
                        <Typography component="span" variant="overline" sx={{ display: "block" }}>
                            Export formats
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5, pr: 1 }}>
                            EDF, PDF and IPC use the selected language. WebHMI writes every language into one JSON file.
                        </Typography>
                    </ListSubheader>
                    <MenuItem>
                        <ExportTextButton
                            language={language}
                            onStart={handleExportStart}
                            onEnd={handleExportEnd}
                            fileExtension={"edf"}
                            mode={"approved"}
                            use_old_code={exportUseOldCodeParam}
                            showExportPrefix={false}
                        />
                    </MenuItem>
                    <MenuItem>
                        <ExportHmiButton
                            targetSystem={"visuplus"}
                            buttonText={"Visu+"}
                            onStart={handleExportStart}
                            onEnd={handleExportEnd}
                            showExportPrefix={false}
                            startQueryParams={{
                                mode: "approved",
                                use_old_code: exportUseOldCodeParam,
                            }}
                        />
                    </MenuItem>
                    <MenuItem>
                        <ExportHmiButton
                            targetSystem={"alarms-xml"}
                            buttonText={"Exor"}
                            onStart={handleExportStart}
                            onEnd={handleExportEnd}
                            showExportPrefix={false}
                            startQueryParams={{
                                mode: "approved",
                                use_old_code: exportUseOldCodeParam,
                            }}
                        />
                    </MenuItem>
                    <MenuItem>
                        <ExportTextButton
                            language={language}
                            onStart={handleExportStart}
                            onEnd={handleExportEnd}
                            fileExtension={"kollmorgen"}
                            mode={"approved"}
                            use_old_code={exportUseOldCodeParam}
                            buttonLabel={"OPTX2"}
                            downloadFileName={"kollmorgen_export.zip"}
                            expectZipResponse={true}
                            hideLanguageInLabel={true}
                            showExportPrefix={false}
                        />
                    </MenuItem>
                    <MenuItem>
                        <ExportTextButton
                            language={language}
                            onStart={handleExportStart}
                            onEnd={handleExportEnd}
                            fileExtension={"pdf"}
                            mode={"approved"}
                            use_old_code={exportUseOldCodeParam}
                            downloadFileName={pdfFileNameForLanguage(language)}
                            buttonLabel={"PDF"}
                            maxAttempts={120}
                            pollingDelayMs={3000}
                            showExportPrefix={false}
                        />
                    </MenuItem>
                    <MenuItem>
                        <ExportHmiButton
                            targetSystem={"webhmi"}
                            buttonText={"WebHMI"}
                            onStart={handleExportStart}
                            onEnd={handleExportEnd}
                            showExportPrefix={false}
                            downloadFileName={"events_webhmi.json"}
                            startQueryParams={{
                                mode: "approved",
                                use_old_code: exportUseOldCodeParam,
                            }}
                        />
                    </MenuItem>
                    <MenuItem>
                        <ExportTextButton
                            language={language}
                            onStart={handleExportStart}
                            onEnd={handleExportEnd}
                            fileExtension={"ipc"}
                            mode={"approved"}
                            use_old_code={exportUseOldCodeParam}
                            buttonLabel={"IPC"}
                            downloadFileName={"events_ipc.yaml"}
                            blobMimeType={"application/x-yaml;charset=utf-8"}
                            showExportPrefix={false}
                        />
                    </MenuItem>

                    {showExperimentalExports ? [
                        <Divider key="exp-divider" component="li" sx={{ my: 0.5 }} />,
                        <ListSubheader
                            key="exp-header"
                            sx={{
                                lineHeight: 1.35,
                                whiteSpace: "normal",
                                py: 1,
                            }}
                        >
                            <Tooltip title={EXPERIMENTAL_EXPORT_TOOLTIP} placement="left" enterDelay={400}>
                                <Typography component="span" variant="overline" sx={{ cursor: "help", display: "block" }}>
                                    Experimental
                                </Typography>
                            </Tooltip>
                            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                                Approved + own drafts for testing only. Not for production.
                            </Typography>
                        </ListSubheader>,
                        <MenuItem key="exp-edf">
                            <ExportTextButton
                                language={language}
                                onStart={handleExportStart}
                                onEnd={handleExportEnd}
                                fileExtension={"edf"}
                                mode={EXPORT_MODE_APPROVED_PLUS_OWN_UNAPPROVED}
                                use_old_code={exportUseOldCodeParam}
                                buttonLabel={"EDF (experimental)"}
                                downloadFileName={"events_export_experimental.edf"}
                                showExportPrefix={false}
                            />
                        </MenuItem>,
                        <MenuItem key="exp-visu">
                            <ExportHmiButton
                                targetSystem={"visuplus"}
                                buttonText={"Visu+ (experimental)"}
                                onStart={handleExportStart}
                                onEnd={handleExportEnd}
                                showExportPrefix={false}
                                startQueryParams={{
                                    mode: EXPORT_MODE_APPROVED_PLUS_OWN_UNAPPROVED,
                                    use_old_code: exportUseOldCodeParam,
                                }}
                                downloadFileName={"events_export_visuplus_experimental.zip"}
                            />
                        </MenuItem>,
                        <MenuItem key="exp-exor">
                            <ExportHmiButton
                                targetSystem={"alarms-xml"}
                                buttonText={"Exor (experimental)"}
                                onStart={handleExportStart}
                                onEnd={handleExportEnd}
                                showExportPrefix={false}
                                startQueryParams={{
                                    mode: EXPORT_MODE_APPROVED_PLUS_OWN_UNAPPROVED,
                                    use_old_code: exportUseOldCodeParam,
                                }}
                                downloadFileName={"events_export_alarms-xml_experimental.xml"}
                            />
                        </MenuItem>,
                        <MenuItem key="exp-optx2">
                            <ExportTextButton
                                language={language}
                                onStart={handleExportStart}
                                onEnd={handleExportEnd}
                                fileExtension={"kollmorgen"}
                                mode={EXPORT_MODE_APPROVED_PLUS_OWN_UNAPPROVED}
                                use_old_code={exportUseOldCodeParam}
                                buttonLabel={"OPTX2 (experimental)"}
                                downloadFileName={"kollmorgen_export_experimental.zip"}
                                expectZipResponse={true}
                                hideLanguageInLabel={true}
                                showExportPrefix={false}
                            />
                        </MenuItem>,
                        <MenuItem key="exp-pdf">
                            <ExportTextButton
                                language={language}
                                onStart={handleExportStart}
                                onEnd={handleExportEnd}
                                fileExtension={"pdf"}
                                mode={EXPORT_MODE_APPROVED_PLUS_OWN_UNAPPROVED}
                                use_old_code={exportUseOldCodeParam}
                                downloadFileName={`experimental_${pdfFileNameForLanguage(language)}`}
                                buttonLabel={"PDF (experimental)"}
                                maxAttempts={120}
                                pollingDelayMs={3000}
                                showExportPrefix={false}
                            />
                        </MenuItem>,
                        <MenuItem key="exp-webhmi">
                            <ExportHmiButton
                                targetSystem={"webhmi"}
                                buttonText={"WebHMI (experimental)"}
                                onStart={handleExportStart}
                                onEnd={handleExportEnd}
                                showExportPrefix={false}
                                downloadFileName={"events_webhmi_experimental.json"}
                                startQueryParams={{
                                    mode: EXPORT_MODE_APPROVED_PLUS_OWN_UNAPPROVED,
                                    use_old_code: exportUseOldCodeParam,
                                }}
                            />
                        </MenuItem>,
                        <MenuItem key="exp-ipc">
                            <ExportTextButton
                                language={language}
                                onStart={handleExportStart}
                                onEnd={handleExportEnd}
                                fileExtension={"ipc"}
                                mode={EXPORT_MODE_APPROVED_PLUS_OWN_UNAPPROVED}
                                use_old_code={exportUseOldCodeParam}
                                buttonLabel={"IPC (experimental)"}
                                downloadFileName={"events_ipc_experimental.yaml"}
                                blobMimeType={"application/x-yaml;charset=utf-8"}
                                showExportPrefix={false}
                            />
                        </MenuItem>,
                    ] : null}
                </Menu>
            }
        </>
    );
};

export default ExportMenu;
